import axios from "axios";
import Spinner from "components/Spinner";
import Toast from "components/Toast/ToastComponent";
import { setNotification } from "features/Notification/notificationSlice";
import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Modal, Row, Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import CustomerAddressEditModal from "./CustomerAddressEditModal";
import authHeader from "services/auth-header";
import API from "services/axios";
import "./CustomerWoApproval.css";
import { PICKUP_ADDRESS, isPickupMethod } from "./pickupAddress";
import ProductInformationButtons from "./ProductInformationButtons";
import RejectionModal from "./RejectionModal";
import {
  TransformComponent,
  TransformWrapper,
  useControls,
} from "react-zoom-pan-pinch";
import { sortProductSizes, stripHtml } from "utils/UtilityFuncs";
import {
  NO_TEAM,
  filterByTeam,
  hasTeamName,
  orderProducts,
  teamOptions,
} from "./productOrder";

// If one of OUR staff is logged into the app in this browser, return their
// identity so the backend can attribute customer-link actions to them
// ("staff via customer link"). A genuine customer opening the emailed link
// has no such session (or role === "customer"), so this returns null for them.
const getInternalUser = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user"));
    if (u && u.accessToken && u.roles && u.roles !== "customer") {
      return {
        id: u.id,
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName,
        role: u.roles,
      };
    }
  } catch (e) {
    /* ignore malformed localStorage */
  }
  return null;
};

export default function CustomerWoApproval() {
  /*   All States
   ********************************************* */
  const { token } = useParams();
  // True while the customer has the address editor open. Only ever set while
  // the card is still pending -- see the button, and the server enforces it
  // too rather than relying on the button being hidden.
  const [editingAddress, setEditingAddress] = useState(false);
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  // Customer-variant links (?linkType=customer, from the "Customer Link"
  // email template placeholder) hide the Billing & Shipping card. The
  // dealer variant (?linkType=dealer, or no param at all — the default/
  // legacy "Approval Link") shows it same as today.
  const isCustomerLink = searchParams.get("linkType") === "customer";

  const [approvalProductsData, setApprovalProductsData] = useState(null);
  const [approvalData, setApprovalData] = useState(null);
  // The company's shipment methods, from the approval fetch, so the Edit
  // shipping modal can offer them.
  const [shipmentMethodsList, setShipmentMethodsList] = useState([]);

  // Single unified state for all approvals
  const [approvalState, setApprovalState] = useState({
    productApprovals: [],
    excelApprovalStatus: "pending_approval",
    excelComments: "",
    shippingAddressStatus: "pending_approval",
    shippingAddressComments: "",
    inHandByDate: "",
  });

  const [loading, setLoading] = useState(true);
  const [colorMap, setColorMap] = useState({});
  const [loadingColors, setLoadingColors] = useState(false);

  // The full-view modal browses a LIST of image paths, not one. `list` is the
  // set to page through (the visible product images, or a single image for a
  // one-off like a colour swatch); `index` is where we are in it.
  const [fullViewList, setFullViewList] = useState([]);
  const [fullViewIndex, setFullViewIndex] = useState(0);
  const [showFullViewImgModal, setShowFullViewImgModal] = useState(false);
  const fullViewImgUrl = fullViewList[fullViewIndex] || null;

  const [lockApproval, setLockApproval] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const loadingOn = () => setLoading(true);
  const loadingOff = () => setLoading(false);

  const [disableFields, setDisableFields] = useState(false);

  // Rejection modal state
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [currentRejectionItem, setCurrentRejectionItem] = useState(null);

  // Filter tab state - defaults to 'all' when everything is already approved,
  // otherwise 'pending'. Decided once the first time data loads (see
  // hasSetDefaultFilterTab below); later refetches never override the
  // user's own tab selection.
  const [activeFilterTab, setActiveFilterTab] = useState("pending");
  // Product order (user, 2026-10-26): "style" = the work order's own order
  // (sortId), as it always was; "team" = team name A-Z, products with no team
  // name last, each group in sortId order. null until the user picks one:
  // Team Name is the default when some product has a team name (user,
  // 2026-10-27), else Style Number.
  const [productOrder, setProductOrder] = useState(null);
  // Team filter at the end of the Sort By row (user, 2026-10-29): "" = all,
  // NO_TEAM = products with no team name, else one team name.
  const [teamFilter, setTeamFilter] = useState("");
  const hasSetDefaultFilterTab = useRef(false);
  const [showApproveAllDateModal, setShowApproveAllDateModal] = useState(false);
  const [approveAllDate, setApproveAllDate] = useState("");

  const [, setUploadingCustomerExcel] = useState(false);
  const [deletingCustomerExcel, setDeletingCustomerExcel] = useState(false);

  const getApprovalDetails = useCallback(
    async (_token) => {
      try {
        loadingOn();

        let viewOnly = searchParams.get("viewOnly") === "true";

        const { data } = await API.get(
          "/wo-lot-approval/" +
            _token +
            `${viewOnly ? "?viewOnly=" + viewOnly : ""}`,
          {
            headers: authHeader(),
          },
        );
        if (data?.success) {
          setApprovalProductsData(data.data);
          setApprovalData(data.approvalData || null);
          setShipmentMethodsList(data.shipmentMethods || []);
          // viewOnly flag from url
          const isViewOnly = searchParams.get("viewOnly") === "true";
          setViewOnly(isViewOnly);

          // Lock approval only when approval record marks allApproved AND the
          // WO's status has "Locks customer approval" ticked in Settings ->
          // WO Statuses (the API works it out; user, 2026-10-29).
          const isLocked = Boolean(
            data?.approvalData?.allApproved && data?.statusLocked,
          );
          setLockApproval(isLocked);
          setDisableFields(data?.approvalData?.allApproved);
        } else {
          dispatch(setNotification({ type: "error", message: data.message }));
        }
        loadingOff();
      } catch (err) {
        loadingOff();
        if (err?.response?.status === 423) {
          // A locked order returns no full payload, but it does echo back the
          // customer's In-Hand date so the approved screen can show it as their
          // estimated shipping date. Keep whatever it sent.
          if (err.response.data?.approvalData) {
            setApprovalData(err.response.data.approvalData);
          }
          setLockApproval(true);
          return;
        }
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          }),
        );
      }
    },
    [dispatch, searchParams],
  );

  const downloadFile = (fileKey) => {
    axios
      .post(`${process.env.REACT_APP_API_URI}/aws/images`, {
        key: fileKey,
      })
      .then((response) => {
        const url = response.data[0];

        const link = document.createElement("a");
        link.href = url;
        // Opens in a new tab (user, 2026-11-02) instead of downloading.
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.log("Error downloading file:", error);
        dispatch(
          setNotification({
            type: "error",
            message: "Failed to download image.",
          }),
        );
      });
  };

  const isValidExcelFile = (file) => {
    if (!file) return false;
    const allowedExtensions = [".xlsx", ".xls"];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    return allowedExtensions.includes(fileExtension);
  };

  const uploadCustomerExcel = async (fileToUpload) => {
    if (!fileToUpload || !isValidExcelFile(fileToUpload)) {
      return false;
    }

    try {
      setUploadingCustomerExcel(true);

      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("approvalLink", token);

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URI}/wo-lot-approvals/upload-customer-excel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (data?.success) {
        await getApprovalDetails(token);
        dispatch(
          setNotification({
            type: "success",
            message: data?.message || "Customer excel uploaded successfully.",
          }),
        );
        return true;
      }

      dispatch(
        setNotification({
          type: "error",
          message: data?.message || "Failed to upload customer excel.",
        }),
      );
      return false;
    } catch (err) {
      dispatch(
        setNotification({
          type: "error",
          message:
            err?.response?.data?.message ||
            err.message ||
            "Failed to upload customer excel.",
        }),
      );
      return false;
    } finally {
      setUploadingCustomerExcel(false);
    }
  };

  const deleteCustomerExcelFile = async () => {
    try {
      setDeletingCustomerExcel(true);

      const { data } = await axios.delete(
        `${process.env.REACT_APP_API_URI}/wo-lot-approvals/customer-excel/${token}`,
      );

      if (data?.success) {
        await getApprovalDetails(token);
        dispatch(
          setNotification({
            type: "success",
            message: data?.message || "Customer excel deleted successfully.",
          }),
        );
      } else {
        dispatch(
          setNotification({
            type: "error",
            message: data?.message || "Failed to delete customer excel.",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          type: "error",
          message:
            err?.response?.data?.message ||
            err.message ||
            "Failed to delete customer excel.",
        }),
      );
    } finally {
      setDeletingCustomerExcel(false);
    }
  };

  // Helper to update product approval in the unified state
  const updateProductApprovalState = (currentState, id, changes) => {
    return {
      ...currentState,
      productApprovals: currentState.productApprovals.map((p) =>
        p.id === id ? { ...p, ...changes } : p,
      ),
    };
  };

  // Handle approve button click - updates local state and saves all
  const handleApproveProduct = async (product) => {
    const updatedState = updateProductApprovalState(approvalState, product.id, {
      approveStatus: "approved",
      comments: "",
    });
    setApprovalState(updatedState);
    await saveAllApprovals(updatedState);
  };

  // Handle reject button click - opens modal
  const handleRejectProduct = (product) => {
    const productColorIds = parseProductColorIds(product?.approvalColors);
    const approvalColorNames = productColorIds
      .map((colorId) => colorMap[colorId]?.name)
      .filter(Boolean)
      .join(", ");

    const styleNo = product?.productStyle || "N/A";
    const color = approvalColorNames || product?.productColor || "N/A";
    const teamName = product?.teamName || "N/A";
    const productLabel = product?.productDescription || product?.productStyle;

    setCurrentRejectionItem({
      type: "product",
      id: product.id,
      name: `${productLabel} (Style No: ${styleNo}, Color: ${color}, Team: ${teamName})`,
      data: product,
    });
    setShowRejectionModal(true);
  };

  // Confirm rejection with optional comments
  const confirmRejection = async (comments, attachmentFile) => {
    if (!currentRejectionItem) return;

    const currentType = currentRejectionItem.type;
    let updatedState;
    const statusValue = "rejected";

    if (currentRejectionItem.type === "product") {
      updatedState = updateProductApprovalState(
        approvalState,
        currentRejectionItem.id,
        {
          approveStatus: statusValue,
          comments: comments || "",
          approvalComments: comments || "",
        },
      );
    } else if (currentRejectionItem.type === "shipping") {
      updatedState = {
        ...approvalState,
        shippingAddressStatus: statusValue,
        shippingAddressComments: comments || "",
      };
    } else if (currentRejectionItem.type === "excel") {
      updatedState = {
        ...approvalState,
        excelApprovalStatus: statusValue,
        excelComments: comments || "",
      };
    }

    setApprovalState(updatedState);
    setCurrentRejectionItem(null);
    await saveAllApprovals(updatedState);

    if (currentType === "excel" && attachmentFile) {
      await uploadCustomerExcel(attachmentFile);
    }
  };

  // Handle approve shipping address
  const handleApproveShipping = async () => {
    const updatedState = {
      ...approvalState,
      shippingAddressStatus: "approved",
      shippingAddressComments: "",
    };
    setApprovalState(updatedState);
    await saveAllApprovals(updatedState);
  };

  // Handle reject shipping address
  const handleRejectShipping = () => {
    setCurrentRejectionItem({
      type: "shipping",
      name: "Billing & Shipping Address",
    });
    setShowRejectionModal(true);
  };

  // Handle approve excel file
  const handleApproveExcel = async () => {
    const updatedState = {
      ...approvalState,
      excelApprovalStatus: "approved",
      excelComments: "",
    };
    setApprovalState(updatedState);
    await saveAllApprovals(updatedState);
  };

  // Handle reject excel file
  const handleRejectExcel = () => {
    setCurrentRejectionItem({
      type: "excel",
      name: "Excel File",
    });
    setShowRejectionModal(true);
  };

  // Handle In-Hand By Date submission
  const handleInHandByDateSubmit = async (date) => {
    if (date && date < minInHandDate) {
      dispatch(
        setNotification({
          type: "error",
          message: `In-Hand Date must be on or after ${formatDate(
            minInHandDate,
          )} (minimum 2 weeks from today).`,
        }),
      );
      return;
    }

    const updatedState = {
      ...approvalState,
      inHandByDate: date,
    };
    setApprovalState(updatedState);
    await saveAllApprovals(updatedState);
  };

  const runApproveAll = async ({ inHandByDate }) => {
    const isPendingStatus = (status) => {
      const normalized = (status || "pending").toLowerCase();
      return normalized === "pending" || normalized === "pending_approval";
    };

    // Update only pending products to approved
    const updatedProductApprovals = approvalState.productApprovals.map((p) => {
      if (!isPendingStatus(p.approveStatus)) {
        return p;
      }

      return {
        ...p,
        approveStatus: "approved",
        comments: "",
        approvalComments: "",
      };
    });

    // Update shipping to approved only when pending
    const updatedShippingStatus = isPendingStatus(
      approvalState.shippingAddressStatus,
    )
      ? "approved"
      : approvalState.shippingAddressStatus;

    // Auto-approve excel only when sheet exists and current status is pending
    const updatedExcelStatus =
      approvalData?.approvalExcelFile &&
      isPendingStatus(approvalState.excelApprovalStatus)
        ? "approved"
        : approvalState.excelApprovalStatus;

    // Keep inHandByDate as-is (NOT auto-approved)

    const updatedState = {
      ...approvalState,
      productApprovals: updatedProductApprovals,
      shippingAddressStatus: updatedShippingStatus,
      shippingAddressComments:
        updatedShippingStatus === "approved"
          ? ""
          : approvalState.shippingAddressComments,
      excelApprovalStatus: updatedExcelStatus,
      excelComments:
        updatedExcelStatus === "approved" ? "" : approvalState.excelComments,
      inHandByDate,
    };

    setApprovalState(updatedState);
    await saveAllApprovals(updatedState);
  };

  // Handle approve all items (approves everything regardless of current status)
  // If In-Hand Date is missing, ask for it first in a modal and then continue.
  const handleApproveAll = async () => {
    const selectedInHandDate =
      approvalState.inHandByDate || approvalData?.inHandByDate || "";

    if (!selectedInHandDate) {
      setApproveAllDate("");
      setShowApproveAllDateModal(true);
      return;
    }

    await runApproveAll({ inHandByDate: selectedInHandDate });
  };

  const handleApproveAllFromModal = async () => {
    if (!approveAllDate) {
      dispatch(
        setNotification({
          type: "error",
          message: "Please select In-Hand Date.",
        }),
      );
      return;
    }

    if (approveAllDate < minInHandDate) {
      dispatch(
        setNotification({
          type: "error",
          message: `In-Hand Date must be on or after ${formatDate(
            minInHandDate,
          )} (minimum 2 weeks from today).`,
        }),
      );
      return;
    }

    setShowApproveAllDateModal(false);
    await runApproveAll({ inHandByDate: approveAllDate });
  };

  const getBadgeVariant = (status) => {
    switch ((status || "").toLowerCase()) {
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      case "pending":
      default:
        return "warning";
    }
  };

  const formatStatusLabel = (status) => {
    const normalized = status || "pending";
    if (normalized.toLowerCase() === "rejected") return "REJECTED";
    return normalized.replace(/_/g, " ").toUpperCase();
  };

  const getLatestComment = (rawComment) => {
    if (!rawComment) return "";

    const extractCommentText = (entry) => {
      if (!entry) return "";
      if (typeof entry === "string") return entry.trim();
      if (typeof entry !== "object") return "";

      const commentText =
        entry.Comments ?? entry.comments ?? entry.comment ?? entry.message;
      return typeof commentText === "string" ? commentText.trim() : "";
    };

    if (typeof rawComment === "string") {
      const trimmed = rawComment.trim();
      if (!trimmed) return "";

      if (
        (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
        (trimmed.startsWith("{") && trimmed.endsWith("}"))
      ) {
        try {
          const parsed = JSON.parse(trimmed);

          if (Array.isArray(parsed)) {
            if (!parsed.length) return "";

            const entriesWithComment = parsed
              .map((entry, index) => {
                const text = extractCommentText(entry);
                if (!text) return null;

                const timestamp =
                  entry && typeof entry === "object" && entry.timestamp
                    ? Date.parse(entry.timestamp)
                    : NaN;

                return {
                  text,
                  index,
                  timestamp: Number.isNaN(timestamp) ? null : timestamp,
                };
              })
              .filter(Boolean);

            if (!entriesWithComment.length) return "";

            const hasValidTimestamps = entriesWithComment.some(
              (item) => item.timestamp !== null,
            );

            if (hasValidTimestamps) {
              entriesWithComment.sort((a, b) => {
                const ta = a.timestamp ?? -Infinity;
                const tb = b.timestamp ?? -Infinity;
                if (ta === tb) return a.index - b.index;
                return ta - tb;
              });
              return entriesWithComment[entriesWithComment.length - 1].text;
            }

            return entriesWithComment[entriesWithComment.length - 1].text;
          }

          const singleComment = extractCommentText(parsed);
          if (singleComment) return singleComment;
        } catch (err) {
          return trimmed;
        }
      }

      return trimmed;
    }

    return extractCommentText(rawComment);
  };

  // Parse product approvalColors column into an array of ids
  const parseProductColorIds = useCallback((raw) => {
    if (!raw) return [];

    try {
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      }
      return [];
    } catch (err) {
      console.error("Failed to parse product color ids", err);
      return [];
    }
  }, []);

  // Fetch colors by aggregated IDs for all products once
  const fetchColorsByIds = useCallback(
    async (colorIds) => {
      if (!colorIds || colorIds.length === 0) {
        setColorMap({});
        return;
      }

      try {
        setLoadingColors(true);
        const { data } = await API.post(
          "/sales-wo-colors-by-ids",
          { colorIds, link: token },
          { headers: authHeader() },
        );

        if (data?.success) {
          const colors = data.data || [];
          const nextMap = colors.reduce((acc, color) => {
            const key = color.colorId || color.id;
            if (key) acc[key] = color;
            return acc;
          }, {});
          setColorMap(nextMap);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to load colors",
              type: "error",
            }),
          );
          setColorMap({});
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
        setColorMap({});
      } finally {
        setLoadingColors(false);
      }
    },
    [dispatch, token],
  );

  // Helper to normalize status for filtering
  const getNormalizedStatus = (status) => {
    const s = (status || "pending").toLowerCase();
    if (s === "approved") return "approved";
    if (s === "rejected") return "rejected";
    if (s === "pending" || s === "pending_approval") return "pending";
    return "pending";
  };

  // Check if a card should be shown based on active filter
  const shouldShowCard = (status) => {
    if (activeFilterTab === "all") return true;
    return getNormalizedStatus(status) === activeFilterTab;
  };

  const saveAllApprovals = async (stateToSave) => {
    const currentState = stateToSave || approvalState;

    // For shipping address changes, we now use the direct string value
    const formattedShippingAddressComments =
      currentState.shippingAddressComments;

    // Format approvals to ensure all required fields are present - no pending status allowed
    const formattedApprovals = currentState.productApprovals.map((p) => {
      return {
        id: p.id,
        approveStatus: p.approveStatus,
        approvalComments:
          p.approveStatus === "rejected"
            ? p.approvalComments || p.comments || ""
            : "",
      };
    });

    const payload = {
      woToken: token,
      approvals: formattedApprovals,
      excelFileStatus: currentState.excelApprovalStatus,
      excelComments:
        currentState.excelApprovalStatus === "rejected"
          ? currentState.excelComments
          : "",
      shippingAddressStatus: currentState.shippingAddressStatus,
      shippingAddressComments: formattedShippingAddressComments,
      inHandByDate: currentState.inHandByDate || "",
      internalUser: getInternalUser(),
    };

    // Log payload for debugging
    console.log("Submitting approval payload:", payload);

    try {
      loadingOn();
      const { data } = await API.post(`/wo-lot-approval/approve`, payload, {
        headers: authHeader(),
      });
      if (data?.success) {
        await getApprovalDetails(token); // Refresh data after save
        dispatch(
          setNotification({
            type: "success",
            message: "Approvals submitted successfully.",
          }),
        );
      } else {
        dispatch(
          setNotification({
            type: "error",
            message: data?.message || "Failed to submit approvals.",
          }),
        );
      }
      loadingOff();
    } catch (err) {
      console.error("API error:", err);
      dispatch(
        setNotification({
          type: "error",
          message:
            err.response?.data?.message ||
            err.message ||
            "Failed to save approvals.",
        }),
      );
      loadingOff();
    }
  };

  // Reset a single approval (product / excel_file / shipping_address / due_date)
  const resetApproval = async (type, productId = null) => {
    try {
      setResetLoading(true);
      const { data } = await API.post(
        `/wo-lot-approval-reset-status/by-link`,
        {
          type,
          woId: approvalProductsData?.woId,
          productId,
          link: token,
          internalUser: getInternalUser(),
        },
        { headers: authHeader() },
      );
      if (data?.success) {
        await getApprovalDetails(token);
        dispatch(
          setNotification({
            message: data?.message || "Reset successful",
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Reset failed",
            type: "error",
          }),
        );
      }
    } catch (err) {
      console.error("Reset error:", err);
      dispatch(
        setNotification({
          message: err.message || "Reset failed",
          type: "error",
        }),
      );
    } finally {
      setResetLoading(false);
    }
  };

  const resetAllApprovals = async () => {
    const allProducts = approvalState.productApprovals || [];
    const shouldResetShipping = Boolean(approvalState.shippingAddressStatus);
    const shouldResetDueDate = true;
    const shouldResetExcel = Boolean(approvalData?.approvalExcelFile);

    if (!shouldResetShipping && !shouldResetExcel && allProducts.length === 0) {
      dispatch(
        setNotification({
          message: "No items found to reset.",
          type: "error",
        }),
      );
      return;
    }

    try {
      setResetLoading(true);

      const resetRequests = [];

      if (shouldResetShipping) {
        resetRequests.push(
          API.post(
            `/wo-lot-approval-reset-status/by-link`,
            {
              type: "shipping_address",
              woId: approvalProductsData?.woId,
              productId: null,
              link: token,
              internalUser: getInternalUser(),
            },
            { headers: authHeader() },
          ),
        );
      }

      if (shouldResetDueDate) {
        resetRequests.push(
          API.post(
            `/wo-lot-approval-reset-status/by-link`,
            {
              type: "due_date",
              woId: approvalProductsData?.woId,
              productId: null,
              link: token,
              internalUser: getInternalUser(),
            },
            { headers: authHeader() },
          ),
        );
      }

      if (shouldResetExcel) {
        resetRequests.push(
          API.post(
            `/wo-lot-approval-reset-status/by-link`,
            {
              type: "excel_file",
              woId: approvalProductsData?.woId,
              productId: null,
              link: token,
              internalUser: getInternalUser(),
            },
            { headers: authHeader() },
          ),
        );
      }

      allProducts.forEach((product) => {
        resetRequests.push(
          API.post(
            `/wo-lot-approval-reset-status/by-link`,
            {
              type: "product",
              woId: approvalProductsData?.woId,
              productId: product.id,
              link: token,
              internalUser: getInternalUser(),
            },
            { headers: authHeader() },
          ),
        );
      });

      const responses = await Promise.allSettled(resetRequests);
      const successCount = responses.filter(
        (res) => res.status === "fulfilled" && res.value?.data?.success,
      ).length;

      if (successCount > 0) {
        await getApprovalDetails(token);
        dispatch(
          setNotification({
            message: `Reset completed for ${successCount} item${successCount > 1 ? "s" : ""}.`,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: "Failed to reset items.",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message || "Failed to reset items.",
          type: "error",
        }),
      );
    } finally {
      setResetLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "C";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  // Format date to ISO Wednesday, December 17, 2025
  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    // The New Date object is created.
    const date = new Date(dateStr);

    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      // CRITICAL: Forces the formatting to use UTC time, preventing day-shifting.
      timeZone: "UTC",
    };

    // CRITICAL: Forces the formatting to use U.S. English (en-US) style and words globally.
    return date.toLocaleDateString("en-US", options);
  };

  // Calculate approval statistics
  const approvalStats = useMemo(() => {
    // Start with product approvals
    const productStats = approvalState.productApprovals.reduce(
      (acc, p) => {
        const status = (p.approveStatus || "pending").toLowerCase();

        if (status === "approved") acc.approved++;
        else if (status === "rejected") acc.rejected++;
        else acc.pending++;

        return acc;
      },
      { pending: 0, approved: 0, rejected: 0 },
    );

    // Add shipping address approval
    const shippingStatus = approvalState.shippingAddressStatus.toLowerCase();
    if (shippingStatus === "approved") productStats.approved++;
    else if (shippingStatus === "rejected") productStats.rejected++;
    else productStats.pending++;

    // Add excel file approval if it exists
    if (approvalData && approvalData.approvalExcelFile) {
      const excelStatus = approvalState.excelApprovalStatus.toLowerCase();
      if (excelStatus === "approved") productStats.approved++;
      else if (excelStatus === "rejected") productStats.rejected++;
      else productStats.pending++;
    }

    // Add In-Hand By Date (required - counts as pending if not set, approved if set)
    if (approvalData?.inHandByDate) {
      productStats.approved++;
    } else {
      productStats.pending++;
    }

    // Calculate totals
    productStats.total =
      approvalState.productApprovals.length +
      1 + // shipping
      1 + // in-hand by date (always required)
      (approvalData && approvalData.approvalExcelFile ? 1 : 0); // excel (if exists)
    productStats.done = productStats.approved + productStats.rejected;
    productStats.allReviewed = productStats.pending === 0;

    return productStats;
  }, [approvalState, approvalData]);

  // Minimum selectable In-Hand Date.
  // For a specific location the customer cannot pick a date less than 2 weeks
  // from today — the date the customer is viewing this page — regardless of
  // the work order's own date; all other locations keep today.
  // NOTE: GATED_LOCATION_ID is 13 for testing — switch to 6 for production.
  const GATED_LOCATION_ID = 3; // 3 is for nova-chris location.
  const MIN_LEAD_DAYS = 14;

  // Whether the 2-week lead-time rule applies to this work order's location.
  const isLeadTimeRestricted =
    Number(approvalProductsData?.locationId) === GATED_LOCATION_ID;

  const minInHandDate = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().split("T")[0];

    if (Number(approvalProductsData?.locationId) === GATED_LOCATION_ID) {
      return new Date(now.getTime() + MIN_LEAD_DAYS * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];
    }

    return todayStr;
  }, [approvalProductsData?.locationId]);

  // Items to show in Reset Modal — ordered to match the card render order on screen:
  // 1. Shipping  2. In-Hand Date  3. Excel (if exists)  4. Products
  const resetModalItems = useMemo(() => {
    const items = [];

    // 1. Shipping: always include
    items.push({
      id: null,
      type: "shipping_address",
      label: "Shipping Address",
      status: (approvalState?.shippingAddressStatus
        ? approvalState?.shippingAddressStatus?.split("_")[0]
        : "pending_approval"
      ).toLowerCase(),
    });

    // 2. In-Hand Date: approved when date is set, pending otherwise
    items.push({
      id: null,
      type: "due_date",
      label: "In-Hand Date",
      status: approvalData?.inHandByDate ? "approved" : "pending",
    });

    // 3. Excel: include only if the excel sheet exists
    if (approvalData?.approvalExcelFile) {
      items.push({
        id: null,
        type: "excel_file",
        label: "Player Numbering Sheet",
        status: (approvalState?.excelApprovalStatus
          ? approvalState?.excelApprovalStatus.split("_")[0]
          : "pending_approval"
        ).toLowerCase(),
      });
    }

    // 4. Products: include ALL with their current status
    (approvalState.productApprovals || []).forEach((p) => {
      items.push({
        id: p.id,
        type: "product",
        label: p.productDescription || p.productStyle || `Product ${p.id}`,
        status: (p.approveStatus || "pending_approval").toLowerCase(),
      });
    });

    return items;
  }, [approvalState, approvalData]);

  /**
   * Open the full-view modal.
   *
   * `list` is the set to browse (defaults to just this image, for one-offs like
   * a colour swatch that are not part of the card gallery). Opening a product
   * image passes galleryImages so prev/next moves between product cards.
   */
  const onFullViewImg = (imgUrl, list) => {
    const arr = Array.isArray(list) && list.length ? list : [imgUrl];
    const idx = arr.indexOf(imgUrl);
    setFullViewList(arr);
    setFullViewIndex(idx >= 0 ? idx : 0);
    setShowFullViewImgModal(true);
  };

  const onHideFullViewImg = () => {
    setShowFullViewImgModal(false);
    setFullViewList([]);
    setFullViewIndex(0);
  };

  // Wrap around so the ends are never dead: after the last image, next returns
  // to the first. Guarded to a no-op for a single-image list.
  const showPrevImg = () =>
    setFullViewIndex((i) =>
      fullViewList.length ? (i - 1 + fullViewList.length) % fullViewList.length : 0,
    );
  const showNextImg = () =>
    setFullViewIndex((i) =>
      fullViewList.length ? (i + 1) % fullViewList.length : 0,
    );

  const Controls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls();

    return (
      <div className="image-controls d-flex align-items-center justify-content-center gap-2">
        <button
          className="control-btn zoom-btn"
          onClick={() => zoomIn()}
          aria-label="Zoom In"
        >
          <i className="mdi mdi-plus"></i>
        </button>
        <button
          className="control-btn zoom-btn"
          onClick={() => zoomOut()}
          aria-label="Zoom Out"
        >
          <i className="mdi mdi-minus"></i>
        </button>
        <button
          className="control-btn reset-btn"
          onClick={() => resetTransform()}
          aria-label="Reset Zoom"
        >
          <i className="mdi mdi-refresh"></i>
        </button>
      </div>
    );
  };

  /*   All UseEffects
   ********************************************* */
  // Local state to manage approvals per product

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (token) getApprovalDetails(token);
    }, 50);
    return () => clearTimeout(timeout);
  }, [token, getApprovalDetails]);

  // Initialize unified approval state when data arrives
  useEffect(() => {
    if (approvalProductsData?.WOProducts || approvalData) {
      const productApprovals = approvalProductsData?.WOProducts
        ? approvalProductsData.WOProducts.map((p) => ({
            id: p.id,
            approveStatus: p.approveStatus || "pending",
            approvalComments: p.approvalComments || "",
            comments: p.approvalComments || "",
            requireNames: p.requireNames || "no",
            requireNumbers: p.requireNumbers || "no",
          }))
        : [];

      setApprovalState({
        productApprovals,
        excelApprovalStatus:
          approvalData?.excelFileStatus || "pending_approval",
        excelComments: approvalData?.excelComments || "",
        shippingAddressStatus:
          approvalData?.shippingAddressStatus || "pending_approval",
        shippingAddressComments: approvalData?.shippingAddressComments || "",
        inHandByDate: approvalData?.inHandByDate || "",
      });

      // Default filter tab: 'all' once everything shown to the customer is
      // already reviewed, otherwise 'pending'. Mirrors approvalStats' own
      // pending count (not the backend's allApproved flag — that can be
      // true while a numbering-sheet/excel approval is still pending) so
      // the default always agrees with the badge counts. Computed here,
      // from the same freshly-fetched values approvalState is built from,
      // and decided once — later refetches (after an approve/reset action)
      // never override the user's own tab selection.
      if (!hasSetDefaultFilterTab.current) {
        hasSetDefaultFilterTab.current = true;
        let pending = productApprovals.filter(
          (p) => (p.approveStatus || "pending").toLowerCase() === "pending",
        ).length;
        const shippingStatus = (
          approvalData?.shippingAddressStatus || "pending_approval"
        ).toLowerCase();
        if (shippingStatus !== "approved" && shippingStatus !== "rejected") {
          pending++;
        }
        if (approvalData?.approvalExcelFile) {
          const excelStatus = (
            approvalData?.excelFileStatus || "pending_approval"
          ).toLowerCase();
          if (excelStatus !== "approved" && excelStatus !== "rejected") {
            pending++;
          }
        }
        if (!approvalData?.inHandByDate) {
          pending++;
        }
        setActiveFilterTab(pending === 0 ? "all" : "pending");
      }
    }
  }, [approvalProductsData, approvalData]);

  // Fetch all unique colors needed by all products in a single request
  useEffect(() => {
    const products = approvalProductsData?.WOProducts || [];
    if (!products.length) {
      setColorMap({});
      return;
    }

    const uniqueIds = new Set();
    products.forEach((p) => {
      parseProductColorIds(p.approvalColors).forEach((id) => {
        if (id !== null && id !== undefined && id !== "") {
          uniqueIds.add(id);
        }
      });
    });

    const idsArray = Array.from(uniqueIds);
    if (idsArray.length > 0) {
      fetchColorsByIds(idsArray);
    } else {
      setColorMap({});
    }
  }, [
    approvalProductsData?.WOProducts,
    fetchColorsByIds,
    parseProductColorIds,
  ]);

  // Switch to 'all' tab when lockApproval is true (already fully approved) or all items are reviewed
  // useEffect(() => {
  //   if (
  //     lockApproval ||
  //     (approvalStats.allReviewed && approvalStats.pending === 0)
  //   ) {
  //     setActiveFilterTab("all");
  //   }
  // }, [lockApproval, approvalStats.allReviewed, approvalStats.pending]);

  // Update page title and meta tags for social sharing
  useEffect(() => {
    if (approvalProductsData?.woId && approvalProductsData?.jobName) {
      const woId = approvalProductsData.woId || approvalProductsData.id;
      const title = `W-${woId} - ${approvalProductsData.jobName}`;
      const description = `Work Order W-${woId} for ${approvalProductsData.customerName || "Customer"} - ${approvalProductsData.jobName}`;

      // Update page title
      document.title = title;

      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.name = "description";
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = description;

      // Update Open Graph tags for better social sharing
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement("meta");
        ogTitle.setAttribute("property", "og:title");
        document.head.appendChild(ogTitle);
      }
      ogTitle.content = title;

      let ogDescription = document.querySelector(
        'meta[property="og:description"]',
      );
      if (!ogDescription) {
        ogDescription = document.createElement("meta");
        ogDescription.setAttribute("property", "og:description");
        document.head.appendChild(ogDescription);
      }
      ogDescription.content = description;
    }
  }, [
    approvalProductsData?.woId,
    approvalProductsData?.jobName,
    approvalProductsData?.customerName,
    approvalProductsData?.id,
  ]);

  // parse contactData JSON into object for Customer Info section
  const parsedContact = useMemo(() => {
    if (!approvalProductsData?.contactData) return null;
    try {
      return typeof approvalProductsData?.contactData === "string"
        ? JSON.parse(approvalProductsData.contactData)
        : approvalProductsData.contactData;
    } catch (e) {
      return null;
    }
  }, [approvalProductsData]);

  // Note: do not return early here; keep hooks order stable. UI shows lock overlay only.

  // Parse shipping address - need double parsing as it's a stringified JSON inside a string
  const parsedShippingAddress = useMemo(() => {
    if (!approvalProductsData?.shippingAddress) return null;
    try {
      // First parse the outer string
      const parsedOuter =
        typeof approvalProductsData.shippingAddress === "string"
          ? JSON.parse(approvalProductsData.shippingAddress)
          : approvalProductsData.shippingAddress;

      // Then parse the inner JSON string
      return typeof parsedOuter === "string"
        ? JSON.parse(parsedOuter)
        : parsedOuter;
    } catch (e) {
      console.error("Error parsing shipping address:", e);
      return null;
    }
  }, [approvalProductsData]);

  const parsedBillingAddress = useMemo(() => {
    if (!approvalProductsData?.billingAddress) return null;
    try {
      const parsed =
        typeof approvalProductsData.billingAddress === "string"
          ? JSON.parse(approvalProductsData.billingAddress)
          : approvalProductsData.billingAddress;
      return typeof parsed === "string" ? JSON.parse(parsed) : parsed;
    } catch (e) {
      console.error("Error parsing billing address:", e);
      return null;
    }
  }, [approvalProductsData]);

  // Products to display - filtered based on active tab
  const filteredProducts = useMemo(() => {
    if (!approvalProductsData?.WOProducts) return [];

    // Helper to get status priority for sorting (lower = higher priority)
    const getStatusPriority = (status) => {
      const normalized = getNormalizedStatus(status);
      if (normalized === "pending") return 0;
      if (normalized === "rejected") return 1;
      if (normalized === "approved") return 2;
      return 3;
    };

    if (activeFilterTab === "all") {
      // Sort by priority: pending first, then rejected, then approved
      return [...approvalProductsData.WOProducts].sort((a, b) => {
        const localA =
          approvalState.productApprovals.find((p) => p.id === a.id) || {};
        const localB =
          approvalState.productApprovals.find((p) => p.id === b.id) || {};
        const statusA = localA.approveStatus || a.approveStatus || "pending";
        const statusB = localB.approveStatus || b.approveStatus || "pending";
        return getStatusPriority(statusA) - getStatusPriority(statusB);
      });
    }

    return approvalProductsData.WOProducts.filter((prod) => {
      const local =
        approvalState.productApprovals.find((p) => p.id === prod.id) || {};
      const currentStatus =
        local.approveStatus || prod.approveStatus || "pending";
      return getNormalizedStatus(currentStatus) === activeFilterTab;
    });
  }, [approvalProductsData, activeFilterTab, approvalState.productApprovals]);

  // Every visible product card's image, in render order -- the array the
  // full-view modal pages through. Built from filteredProducts so it tracks the
  // active tab: prev/next walks exactly the cards on screen.
  // filteredProducts in the order the cards are drawn (productOrder). A copy:
  // the memo's own array is never sorted in place. With no team name on any
  // card, "Sort By" is hidden and the order is Style No# whatever was picked.
  const hasTeamNames = useMemo(
    () => hasTeamName(filteredProducts),
    [filteredProducts],
  );
  // The team filter lists only the teams on the active tab (Pending shows the
  // teams that still have pending products, etc.), not every team on the
  // W-Order (user, 2026-11-02). The arrows wrap around these, in order.
  const teamChoices = useMemo(() => teamOptions(filteredProducts), [filteredProducts]);
  // A team picked on one tab may not be on the next: fall back to All Teams.
  useEffect(() => {
    if (teamFilter && !teamChoices.some(([value]) => value === teamFilter)) setTeamFilter("");
  }, [teamChoices, teamFilter]);
  const stepTeam = (dir) => {
    const at = teamChoices.findIndex(([value]) => value === teamFilter);
    const next = (Math.max(at, 0) + dir + teamChoices.length) % teamChoices.length;
    setTeamFilter(teamChoices[next][0]);
  };
  const orderedProducts = useMemo(
    () =>
      orderProducts(
        hasTeamNames ? filterByTeam(filteredProducts, teamFilter) : filteredProducts,
        hasTeamNames ? productOrder || "team" : "style",
      ),
    [filteredProducts, productOrder, hasTeamNames, teamFilter],
  );

  const galleryImages = useMemo(
    () => orderedProducts.map((p) => p.productImage).filter(Boolean),
    [orderedProducts],
  );

  // Calculate product style summary (grouped by productStyle with total quantities)
  const productStyleSummary = useMemo(() => {
    if (!approvalProductsData?.WOProducts) return [];

    const styleMap = {};

    approvalProductsData.WOProducts.forEach((prod) => {
      const style = prod.productStyle?.trim() || "No Style Number";
      const qty = parseInt(prod.totalQuantity, 10) || 0;

      if (styleMap[style]) {
        styleMap[style] += qty;
      } else {
        styleMap[style] = qty;
      }
    });

    // Convert to array and sort alphabetically (with "No Style Number" at the end)
    return Object.entries(styleMap)
      .sort((a, b) => {
        if (a[0] === "No Style Number") return 1;
        if (b[0] === "No Style Number") return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([style, qty]) => ({ style, qty }));
  }, [approvalProductsData]);

  if (loading && !approvalProductsData) {
    return <Spinner />;
  }

  if (lockApproval && !viewOnly) {
    return (
      <section
        className="flex-center"
        style={{
          height: "100vh",
        }}
      >
        <div>
          <div className="card">
            <div className="card-body text-center">
              <h2 className="card-title">
                <i className="mdi mdi-check-circle text-success me-2"></i>
                Order Approved & In Production
              </h2>
              <p className="card-text text-muted">
                This work order has been approved and is currently in
                production. No further modifications are permitted at this
                stage.
              </p>

              {/* The date the customer chose as their In-Hand date, shown back
                  to them on the approved screen as their estimated shipping
                  date. Guarded: the lock screen is also reached on a 423 with
                  no approvalData loaded, and an order can be locked without a
                  date having been set. */}
              {approvalData?.inHandByDate && (
                <div className="d-inline-flex align-items-center gap-2 bg-success bg-opacity-10 border border-success rounded-3 px-3 py-2 my-2">
                  <i className="mdi mdi-truck-fast-outline text-success fs-3"></i>
                  <span className="text-start">
                    <span className="d-block small text-muted">
                      Estimated In-Hand Date
                    </span>
                    <span className="fw-bold fs-5 text-success">
                      {formatDate(approvalData.inHandByDate)}
                    </span>
                  </span>
                </div>
              )}

              <p className="card-text small text-secondary">
                For any inquiries or changes, please contact the production team
                or your account administrator.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <Container
        fluid
        className="pt-4 px-md-5 px-3 "
        style={{
          position: "relative",
          background: `
          radial-gradient(circle at 10% 20%, rgba(216, 241, 230, 0.4) 0%, rgba(216, 241, 230, 0) 90%),
          radial-gradient(circle at 80% 30%, rgba(173, 216, 230, 0.3) 0%, rgba(173, 216, 230, 0) 70%),
          radial-gradient(circle at 30% 70%, rgba(230, 230, 250, 0.35) 0%, rgba(230, 230, 250, 0) 80%),
          radial-gradient(circle at 90% 85%, rgba(173, 216, 230, 0.25) 0%, rgba(173, 216, 230, 0) 70%),
          #f8f9fa
        `,
          minHeight: "100vh",
          backgroundAttachment: "fixed",
        }}
      >
        <section className="">
          {/* Responsive container for all content including header */}
          <div className="approval-cards-container mx-auto">
            {approvalProductsData?.customer?.approvalImage && (
              <div className="approval-banner-strip mb-3">
                {/* An 8:1 strip (see CustomerWoApproval.css). Artwork at the
                    asked-for 2400x300 fills it edge to edge with the cards
                    below; anything smaller is centred at its own size rather
                    than stretched. The image must NOT carry width:100% here --
                    that is what upscaled a small logo -- so it is capped by
                    max-width/max-height and left to size itself. */}
                <ImageWIthSignedUrl
                  path={approvalProductsData.customer.approvalImage}
                  imgStyle={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    display: "block",
                    borderRadius: "0.5rem",
                  }}
                />
              </div>
            )}
            {/* compact banner */}
            <Row className="mb-3 align-items-center px-2">
              <Col className=" bg-white shadow-sm px-3 border-start border-primary border-4 rounded-3 compact align-items-center d-flex justify-content-between p-2 rounded">
                <div>
                  <div className="d-none d-md-flex">
                    <div className="d-flex align-items-start w-100 ">
                      <div className="avatar-circle me-3">
                        {getInitials(approvalProductsData?.customerName)}
                      </div>
                      <div className="flex-grow-1 d-flex align-items-start gap-1 gap-md-3 flex-wrap">
                        <div className="d-flex align-items-start flex-column justify-content-start">
                          <div className="fs-4 fs-md-3 fw-bold">
                            W-
                            {approvalProductsData?.woId ||
                              approvalProductsData?.id}
                          </div>
                        </div>
                        <div className="d-flex align-items-start flex-column justify-content-start">
                          <div className="fs-4 fs-md-3 fw-bold">
                            {approvalProductsData?.jobName || ""}
                          </div>
                          <div className="fs-5 fs-md-4 text-muted">
                            {approvalProductsData?.customerName || "Customer"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-md-none">
                    <div className="row gap-1 align-items-center w-100">
                      <div className="col-1 me-2">
                        <div className="avatar-circle-small ">
                          {getInitials(approvalProductsData?.customerName)}
                        </div>
                      </div>
                      <div className="col-auto">
                        <div className="fs-4 fs-md-3 fw-bold">
                          W-
                          {approvalProductsData?.woId ||
                            approvalProductsData?.id}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="fs-4 fs-md-3 fw-bold">
                        {approvalProductsData?.jobName || ""}
                      </div>
                      <div className="fs-5 fs-md-4 text-muted">
                        {approvalProductsData?.customerName || "Customer"}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

          </div>
        </section>

        <section
          style={{
            position: "relative",
          }}
        >
          {/* {lockApproval && (
            <div className="approval-lock-screen-overlay">
              <h1>Already Approved</h1>
            </div>
          )} */}
          {loading && <Spinner />}
          <Toast />
          <section className="">
            {/* Responsive container for all content including header */}
            <div className="approval-cards-container mx-auto">
              {/* Filter Tabs and Approve All Button */}
              <Row className="pb-3">
                <Col>
                  <div className="bg-white shadow-sm p-3 rounded-3">
                    <div className="d-flex flex-column flex-md-row  align-items-center justify-content-between gap-2 ">
                      {/* Filter Tabs */}
                      <div className="d-flex flex-wrap gap-2 align-items-center flex-wrap justify-content-center ">
                        <span
                          className={`badge bg-warning text-dark fs-6 px-3 py-2 cursor-pointer ${
                            activeFilterTab === "pending"
                              ? "ring-2 ring-warning shadow-sm"
                              : ""
                          }`}
                          style={{
                            cursor: "pointer",
                            outline:
                              activeFilterTab === "pending"
                                ? "3px solid #ffc107"
                                : "none",
                            outlineOffset: "2px",
                          }}
                          onClick={() => setActiveFilterTab("pending")}
                        >
                          <i className="mdi mdi-clock-outline me-1"></i>
                          Pending ({approvalStats.pending})
                        </span>
                        <span
                          className={`badge bg-danger fs-6 px-3 py-2 cursor-pointer ${
                            activeFilterTab === "rejected"
                              ? "ring-2 ring-danger shadow-sm"
                              : ""
                          }`}
                          style={{
                            cursor: "pointer",
                            outline:
                              activeFilterTab === "rejected"
                                ? "3px solid #dc3545"
                                : "none",
                            outlineOffset: "2px",
                          }}
                          onClick={() => setActiveFilterTab("rejected")}
                        >
                          <i className="mdi mdi-close-circle me-1"></i>
                          Rejected ({approvalStats.rejected})
                        </span>
                        <span
                          className={`badge bg-success fs-6 px-3 py-2 cursor-pointer ${
                            activeFilterTab === "approved"
                              ? "ring-2 ring-success shadow-sm"
                              : ""
                          }`}
                          style={{
                            cursor: "pointer",
                            outline:
                              activeFilterTab === "approved"
                                ? "3px solid #198754"
                                : "none",
                            outlineOffset: "2px",
                          }}
                          onClick={() => setActiveFilterTab("approved")}
                        >
                          <i className="mdi mdi-check-circle me-1"></i>
                          Approved ({approvalStats.approved})
                        </span>
                        <span
                          className={`badge bg-secondary fs-6 px-3 py-2 cursor-pointer ${
                            activeFilterTab === "all"
                              ? "ring-2 ring-secondary shadow-sm"
                              : ""
                          }`}
                          style={{
                            cursor: "pointer",
                            outline:
                              activeFilterTab === "all"
                                ? "3px solid #6c757d"
                                : "none",
                            outlineOffset: "2px",
                          }}
                          onClick={() => setActiveFilterTab("all")}
                        >
                          <i className="mdi mdi-format-list-bulleted me-1"></i>
                          All ({approvalStats.total})
                        </span>
                      </div>
                      {/* Right side controls */}
                      <div className="mt-2 ms-auto d-flex align-items-center gap-2">
                        <Button
                          variant="warning"
                          size="sm"
                          disabled={resetLoading}
                          onClick={resetAllApprovals}
                        >
                          <i className="mdi mdi-refresh me-1"></i>
                          Reset All
                        </Button>
                      </div>
                    </div>
                    {/* Product Style Summary */}
                    {productStyleSummary.length > 0 && (
                      <div className="mt-3 pt-3 border-top">
                        <div className="fw-bold mb-2 fs-5">
                          <i className="mdi mdi-format-list-bulleted me-1"></i>
                          Product Summary By Style Numbers:
                        </div>
                        <div
                          className="d-flex flex-wrap gap-2"
                          style={{ lineHeight: "1.8" }}
                        >
                          {productStyleSummary.map(({ style, qty }, idx) => (
                            <span key={style} className="text-muted">
                              <span className="fw-semibold text-dark">
                                {style} :
                              </span>{" "}
                              {qty} pcs
                              {idx < productStyleSummary.length - 1 && ","}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Approve All Button */}
                    {/* <div className="my-2 d-md-none">
                      <Button
                        variant="success"
                        size="md"
                        disabled={disableFields || approvalStats.pending === 0}
                        onClick={handleApproveAll}
                        className="fw-bold px-4 btn-mobile-full"
                      >
                        <i className="mdi mdi-check-all me-2"></i>
                        Approve All
                      </Button>
                    </div> */}
                  </div>
                </Col>
              </Row>
              {/* Below the tabs card (user, 2026-10-30). Estimated shipping date, shown once the customer has approved
                  everything and set an In-Hand date -- their own selected date,
                  echoed back so the approved order confirms when it is expected.
                  This is the pre-production state (all approved, WO not yet moved
                  to processing); the locked "In Production" screen shows the same
                  thing after that. Hidden while anything is still pending or
                  rejected, or before a date is chosen. */}
              {approvalData?.inHandByDate &&
                approvalStats.pending === 0 &&
                approvalStats.rejected === 0 && (
                  <Row className="mb-3 px-2">
                    <Col className="bg-success bg-opacity-10 border border-success rounded-3 d-flex align-items-center gap-3 p-3">
                      <i className="mdi mdi-truck-fast-outline text-success fs-1"></i>
                      <div>
                        <div className="small text-muted">
                          <i className="mdi mdi-check-circle text-success me-1"></i>
                          Order approved — estimated in-hand date
                        </div>
                        <div className="fw-bold fs-4 text-success">
                          {formatDate(approvalData.inHandByDate)}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              {/* two-column layout: main + sidebar */}
              <Row>
                <Col lg={12} className="">
                  {/* Contact & Shipping Section */}
                  {!isCustomerLink &&
                    shouldShowCard(approvalState.shippingAddressStatus) && (
                    <div
                      className={`product-item-vertical rounded-3 mb-3 overflow-hidden ${
                        approvalState.shippingAddressStatus !==
                        "pending_approval"
                          ? `bg-${getBadgeVariant(
                              approvalState.shippingAddressStatus,
                            )} bg-opacity-10 border border-2 border-${getBadgeVariant(
                              approvalState.shippingAddressStatus,
                            )}`
                          : ""
                      }`}
                    >
                      {/* Status Row at Top */}
                      <div
                        className={`w-100 text-center py-2 px-3 bg-${getBadgeVariant(
                          approvalState.shippingAddressStatus,
                        )} ${
                          getBadgeVariant(
                            approvalState.shippingAddressStatus,
                          ) !== "warning" && " bg-opacity-25 text-white"
                        }`}
                      >
                        <span
                          className={`fw-bold text-${getBadgeVariant(
                            approvalState.shippingAddressStatus,
                          )} ${
                            getBadgeVariant(
                              approvalState.shippingAddressStatus,
                            ) === "warning"
                              ? "text-white"
                              : ""
                          } text-uppercase`}
                        >
                          {formatStatusLabel(
                            approvalState.shippingAddressStatus,
                          ) || "PENDING APPROVAL"}
                        </span>
                      </div>

                      <div className="product-header">
                        <div className="product-type-label" style={{}}>
                          Billing & Shipping
                        </div>
                        {/* {approvalProductsData?.shippingMethod?.shippingMethod
                          ?.toLowerCase()
                          ?.includes("pickup") && (
                          <div className="flex-center ms-3">
                            <h4>Shipping Method : </h4>
                            <h4 className="ms-1 fw-bold text-primary">
                              {
                                approvalProductsData?.shippingMethod
                                  ?.shippingMethod
                              }{" "}
                            </h4>
                          </div>
                        )} */}
                        <div className="flex-center ms-3 my-2 btn-mobile-full w-50 ">
                          <span className="fw-bold">
                            ( Shipping Method:{" "}
                            {approvalProductsData?.shippingMethod
                              ?.shippingMethod
                              ? approvalProductsData?.shippingMethod
                                  ?.shippingMethod
                              : "N/A"}
                            )
                          </span>
                        </div>
                      </div>
                      <div
                        className="product-body"
                        style={{ minHeight: "unset" }}
                      >
                        {/* Left section (info) with same ratio as product cards */}
                        <div className="product-info">
                          <div className="px-3 py-2">
                            <div className="d-flex flex-column flex-md-row">
                              {/* Contact Info - 33% */}
                              <div
                                className="pe-md-3 mb-3 mb-md-0"
                                style={{ flex: "1" }}
                              >
                                <h4
                                  className="mb-2"
                                  style={{
                                    borderBottom: "2px solid gray",
                                    paddingBottom: "8px",
                                    display: "inline-block",
                                  }}
                                >
                                  Contact Information
                                </h4>
                                <div className="fs-5 fs-md-4">
                                  {parsedContact ? (
                                    <>
                                      <div>
                                        <strong>Name:</strong>{" "}
                                        {parsedContact.firstName}{" "}
                                        {parsedContact.lastName}
                                      </div>
                                      {parsedContact.email && (
                                        <div>
                                          <strong>Email:</strong>{" "}
                                          {parsedContact.email}
                                        </div>
                                      )}
                                      {parsedContact.phone && (
                                        <div>
                                          <strong>Phone:</strong>{" "}
                                          {parsedContact.phone}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div>No contact data available</div>
                                  )}
                                </div>
                              </div>

                              {/* Billing Address - 33% */}
                              <div
                                className="ps-md-3 mb-3 mb-md-0 shipping-address-section-inline"
                                style={{ flex: "1" }}
                              >
                                <h4
                                  className="mb-2"
                                  style={{
                                    borderBottom: "2px solid gray",
                                    paddingBottom: "8px",
                                    display: "inline-block",
                                  }}
                                >
                                  Billing Address
                                </h4>
                                <div className="fs-5 fs-md-4">
                                  {parsedBillingAddress ? (
                                    <>
                                      <div>
                                        {parsedBillingAddress.addressCompany}
                                      </div>
                                      <div>
                                        {parsedBillingAddress.addressLine1 ||
                                          parsedBillingAddress.address1}
                                      </div>
                                      {(parsedBillingAddress.addressLine2 ||
                                        parsedBillingAddress.address2) && (
                                        <div>
                                          {parsedBillingAddress.addressLine2 ||
                                            parsedBillingAddress.address2}
                                        </div>
                                      )}
                                      <div>
                                        {parsedBillingAddress.city},{" "}
                                        {parsedBillingAddress.state},{" "}
                                        {parsedBillingAddress.zip},{" "}
                                        {parsedBillingAddress.country}
                                      </div>
                                    </>
                                  ) : (
                                    <div>No billing address available</div>
                                  )}
                                </div>
                              </div>

                              {/* Shipping Address - 33% */}
                              <div
                                className="px-md-3 mb-3 mb-md-0 "
                                style={{
                                  flex: "1",
                                  borderLeft: "none",
                                  borderRight: "none",
                                }}
                              >
                                <h4
                                  className="mb-2"
                                  style={{
                                    borderBottom: "2px solid gray",
                                    paddingBottom: "8px",
                                    display: "inline-block",
                                  }}
                                >
                                  Shipping Address
                                </h4>
                                <div className="fs-5 fs-md-4">
                                  {parsedShippingAddress ? (
                                    <>
                                      {isPickupMethod(
                                        approvalProductsData?.shippingMethod
                                          ?.shippingMethod,
                                      ) ? (
                                        /* Read from the shared constant, not
                                           typed again: the edit modal fills
                                           the saved address from the same
                                           object, and two copies of a street
                                           address drift the first time the
                                           unit moves. */
                                        <div>
                                          <span className="fw-bold">
                                            (Customer Pickup){" "}
                                          </span>
                                          <br />
                                          {PICKUP_ADDRESS.addressCompany} <br />
                                          {PICKUP_ADDRESS.addressLine1} <br />
                                          {PICKUP_ADDRESS.city},{" "}
                                          {PICKUP_ADDRESS.state}{" "}
                                          {PICKUP_ADDRESS.zip}
                                        </div>
                                      ) : (
                                        <>
                                          <div>
                                            {parsedShippingAddress.addressCompany ||
                                              parsedShippingAddress.address1}
                                          </div>
                                          <div>
                                            {parsedShippingAddress.addressLine1 ||
                                              parsedShippingAddress.address1}
                                          </div>
                                          {(parsedShippingAddress.addressLine2 ||
                                            parsedShippingAddress.address2) && (
                                            <div>
                                              {parsedShippingAddress.addressLine2 ||
                                                parsedShippingAddress.address2}
                                            </div>
                                          )}
                                          <div>
                                            {parsedShippingAddress.city},{" "}
                                            {parsedShippingAddress.state},{" "}
                                            {parsedShippingAddress.zip},{" "}
                                            {parsedShippingAddress.country}
                                          </div>
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <div>No shipping address available</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right section (approval) with same ratio as product cards */}
                        <div
                          className="product-approval"
                          style={{
                            width: "300px",
                          }}
                        >
                          <div className="approval-status-control">
                            {/* Approve/Reject Buttons */}
                            <div className="d-grid gap-2">
                              <Button
                                variant="success"
                                size="md"
                                disabled={
                                  disableFields ||
                                  approvalState.shippingAddressStatus !==
                                    "pending_approval"
                                }
                                onClick={handleApproveShipping}
                                className="fw-bold"
                                style={{
                                  display:
                                    activeFilterTab === "approved" ||
                                    activeFilterTab === "rejected" ||
                                    (activeFilterTab === "all" &&
                                      getNormalizedStatus(
                                        approvalState.shippingAddressStatus,
                                      ) !== "pending")
                                      ? "none"
                                      : "block",
                                }}
                              >
                                <i className="mdi mdi-check-circle me-2"></i>
                                Approve
                              </Button>
                              <Button
                                variant="danger"
                                size="md"
                                disabled={
                                  disableFields ||
                                  approvalState.shippingAddressStatus !==
                                    "pending_approval"
                                }
                                onClick={handleRejectShipping}
                                className="fw-bold"
                                style={{
                                  display:
                                    activeFilterTab === "approved" ||
                                    activeFilterTab === "rejected" ||
                                    (activeFilterTab === "all" &&
                                      getNormalizedStatus(
                                        approvalState.shippingAddressStatus,
                                      ) !== "pending")
                                      ? "none"
                                      : "block",
                                }}
                              >
                                <i className="mdi mdi-close-circle me-2"></i>
                                Reject
                              </Button>

                              {/* Edit, while this card is still PENDING.
                                  Once approved the address is settled and
                                  changing it silently would undo what was
                                  agreed -- the server refuses it too, rather
                                  than relying on this button being hidden.

                                  SHIPPING ONLY. It is gated on
                                  shippingAddressStatus and the modal below is
                                  passed currentShipping and nothing else, so
                                  billing is not editable from this card --
                                  which is why the label says so. */}
                              {getNormalizedStatus(
                                approvalState.shippingAddressStatus,
                              ) === "pending" &&
                                !disableFields && (
                                  <Button
                                    variant="outline-primary"
                                    size="md"
                                    onClick={() => setEditingAddress(true)}
                                    className="fw-bold mt-2"
                                  >
                                    <i className="mdi mdi-pencil me-2"></i>
                                    Edit Shipping Address
                                  </Button>
                                )}

                              {getNormalizedStatus(
                                approvalState.shippingAddressStatus,
                              ) !== "pending" && (
                                <Button
                                  variant="warning"
                                  size="md"
                                  disabled={resetLoading}
                                  onClick={() =>
                                    resetApproval("shipping_address")
                                  }
                                  className="fw-bold mt-2 text-dark"
                                >
                                  <i className="mdi mdi-refresh me-2"></i>
                                  Reset
                                </Button>
                              )}
                            </div>

                            {/* Show rejection or approval with changes comments */}
                            {approvalState.shippingAddressStatus ===
                              "rejected" &&
                              getLatestComment(
                                approvalState.shippingAddressComments,
                              ) && (
                                <div
                                  className="mt-3 p-2 bg-light border rounded"
                                  style={{
                                    borderColor: "#dc3545",
                                  }}
                                >
                                  <small className="text-muted d-block mb-1">
                                    <strong>Rejection Reason:</strong>
                                  </small>
                                  <small>
                                    {getLatestComment(
                                      approvalState.shippingAddressComments,
                                    )}
                                  </small>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* In-Hand By Date Section */}
                  {shouldShowCard(
                    approvalData?.inHandByDate ? "approved" : "pending",
                  ) && (
                    <div
                      className={`product-item-vertical rounded-3 mb-3 overflow-hidden ${
                        approvalData?.inHandByDate
                          ? `bg-success bg-opacity-10 border border-2 border-success`
                          : ""
                      }`}
                    >
                      {/* Status Row at Top */}
                      <div
                        className={`w-100 text-center py-2 px-3 ${
                          approvalData?.inHandByDate
                            ? "bg-success bg-opacity-25"
                            : "bg-warning"
                        }`}
                      >
                        <span
                          className={`fw-bold ${
                            approvalData?.inHandByDate
                              ? "text-success"
                              : "text-white"
                          } text-uppercase`}
                        >
                          {approvalData?.inHandByDate ? "SUBMITTED" : "PENDING"}
                        </span>
                      </div>

                      <div className="product-header">
                        <div className="product-type-label">
                          In-Hand Date
                        </div>
                        <div className="ms-3 flex-center">
                          <h5 className="text-muted mb-0">
                            When do you need this order?
                          </h5>
                        </div>
                      </div>

                      <div
                        className="product-body"
                        style={{ minHeight: "unset" }}
                      >
                        {/* Left section (info) */}
                        <div className="product-info">
                          <div className="px-3 py-3">
                            <div className="d-flex flex-column">
                              <p className="text-muted mb-3 fs-5">
                                Please select the date by which you need this
                                order delivered. This helps us ensure on time
                                delivery.
                              </p>
                              {isLeadTimeRestricted &&
                                !approvalData?.inHandByDate && (
                                  <div className="alert alert-info d-flex align-items-start gap-2 py-2 px-3 mb-3">
                                    <i className="mdi mdi-information-outline fs-3 mt-0"></i>
                                    <span className="fs-5">
                                      This order requires a minimum lead time of
                                      2 weeks. The earliest available In-Hand
                                      Date is{" "}
                                      <strong>
                                        {formatDate(minInHandDate)}
                                      </strong>
                                      .
                                    </span>
                                  </div>
                                )}
                              {approvalData?.inHandByDate && (
                                <div className="d-flex align-items-center gap-2">
                                  <i className="mdi mdi-calendar-check text-success fs-2"></i>
                                  <span className="fs-2 fw-bold text-success">
                                    {formatDate(approvalData.inHandByDate)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right section (date picker) */}
                        <div
                          className="product-approval"
                          style={{ width: "300px" }}
                        >
                          <div className="approval-status-control">
                            {approvalData?.inHandByDate ? (
                              <div className="d-grid gap-2">
                                <Button
                                  variant="warning"
                                  size="md"
                                  disabled={resetLoading}
                                  onClick={() => resetApproval("due_date")}
                                  className="fw-bold mt-2 text-dark"
                                >
                                  <i className="mdi mdi-refresh me-2"></i>
                                  Reset
                                </Button>
                              </div>
                            ) : (
                              <div className="d-grid gap-2">
                                <label className="form-label fw-bold mb-1">
                                  Select Date
                                </label>
                                <input
                                  type="date"
                                  className="form-control form-control-lg"
                                  value={approvalState.inHandByDate || ""}
                                  onChange={(e) =>
                                    setApprovalState((prev) => ({
                                      ...prev,
                                      inHandByDate: e.target.value,
                                    }))
                                  }
                                  min={minInHandDate}
                                  disabled={disableFields}
                                />
                                <Button
                                  variant="success"
                                  size="md"
                                  disabled={
                                    disableFields || !approvalState.inHandByDate
                                  }
                                  onClick={() =>
                                    handleInHandByDateSubmit(
                                      approvalState.inHandByDate,
                                    )
                                  }
                                  className="fw-bold mt-2"
                                >
                                  <i className="mdi mdi-check-circle me-2"></i>
                                  Submit Date
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Excel File Approval Section */}
                  {approvalData &&
                    approvalData?.approvalExcelFile &&
                    shouldShowCard(approvalState.excelApprovalStatus) && (
                      <div
                        className={`product-item-vertical rounded-3 mb-3 overflow-hidden ${
                          approvalState.excelApprovalStatus !==
                            "pending_approval" &&
                          approvalState.excelApprovalStatus !== "0"
                            ? `bg-${getBadgeVariant(
                                approvalState.excelApprovalStatus,
                              )} bg-opacity-10 border border-2 border-${getBadgeVariant(
                                approvalState.excelApprovalStatus,
                              )}`
                            : ""
                        }`}
                      >
                        {/* Status Row at Top */}
                        <div
                          className={`w-100 text-center py-2 px-3 bg-${getBadgeVariant(
                            approvalState.excelApprovalStatus,
                          )} ${
                            getBadgeVariant(
                              approvalState.excelApprovalStatus,
                            ) !== "warning" && " bg-opacity-25 text-white"
                          }`}
                        >
                          <span
                            className={`fw-bold text-${getBadgeVariant(
                              approvalState.excelApprovalStatus,
                            )} ${
                              getBadgeVariant(
                                approvalState.excelApprovalStatus,
                              ) === "warning"
                                ? "text-white"
                                : ""
                            } text-uppercase`}
                          >
                            {formatStatusLabel(
                              approvalState.excelApprovalStatus,
                            ) || "PENDING APPROVAL"}
                          </span>
                        </div>

                        <div className="product-header d-flex justify-content-between align-items-center">
                          <div className="product-type-label" style={{}}>
                            Player Numbering Sheet
                          </div>
                          <div
                            className="product-approval border-start-0"
                            style={{
                              width: "300px",
                            }}
                          >
                            <div className="approval-status-control"></div>
                          </div>
                        </div>

                        <div
                          className="product-body"
                          style={{ minHeight: "unset" }}
                        >
                          {/* Left section (info) with same ratio as product cards */}
                          <div className="product-info">
                            <div className="px-3 py-2">
                              <div className="d-flex flex-column align-items-start gap-1">
                                {/* File name with download button */}
                                <div
                                  onClick={() =>
                                    downloadFile(
                                      approvalData?.approvalExcelFile,
                                    )
                                  }
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  className="fs-4 d-flex align-items-center flex-wrap gap-2"
                                >
                                  <span className="me-2">
                                    {approvalData?.approvalExcelFile
                                      ?.split("/")
                                      .pop()}
                                  </span>
                                </div>
                                <style>
                                  {`
                                    .full-width-md {
                                      width: 100%;
                                    }
                                    @media (min-width: 768px) {
                                      .full-width-md {
                                        width: 260px;
                                      }
                                    }
                                  `}
                                </style>
                                {approvalState.excelApprovalStatus ===
                                  "pending_approval" && (
                                  <button
                                    className="fw-bold btn btn-success full-width-md "
                                    // style={{
                                    //   width: 260,
                                    // }}
                                    disabled={lockApproval}
                                    onClick={() =>
                                      downloadFile(
                                        approvalData?.approvalExcelFile,
                                      )
                                    }
                                  >
                                    <i className="mdi mdi-download me-1"></i>
                                    Download
                                  </button>
                                )}

                                {approvalData?.customerExcelFile && (
                                  <div className="w-100 d-flex flex-column gap-2">
                                    <hr className="w-100 my-2" />

                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                      <span className="fw-semibold text-muted">
                                        Your attached file:
                                      </span>
                                      <div className="d-flex gap-2">
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() =>
                                            downloadFile(
                                              approvalData?.customerExcelFile,
                                            )
                                          }
                                        >
                                          <i className="mdi mdi-download me-1"></i>
                                          Download Your File
                                        </Button>
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          disabled={
                                            disableFields ||
                                            lockApproval ||
                                            deletingCustomerExcel
                                          }
                                          onClick={deleteCustomerExcelFile}
                                        >
                                          <i className="mdi mdi-delete me-1"></i>
                                          {deletingCustomerExcel
                                            ? "Deleting..."
                                            : "Delete"}
                                        </Button>
                                      </div>
                                    </div>
                                    <div className="text-break small">
                                      {approvalData?.customerExcelFile
                                        ?.split("/")
                                        .pop()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Right section (approval) with same ratio as product cards */}
                          <div
                            className="product-approval"
                            style={{ width: "300px" }}
                          >
                            <div className="approval-status-control">
                              {/* Approve/Reject Buttons */}
                              <div className="d-grid gap-2">
                                <Button
                                  variant="success"
                                  size="md"
                                  disabled={
                                    disableFields ||
                                    approvalState.excelApprovalStatus !==
                                      "pending_approval"
                                  }
                                  onClick={handleApproveExcel}
                                  className="fw-bold"
                                  style={{
                                    display:
                                      activeFilterTab === "approved" ||
                                      activeFilterTab === "rejected" ||
                                      (activeFilterTab === "all" &&
                                        getNormalizedStatus(
                                          approvalState.excelApprovalStatus,
                                        ) !== "pending")
                                        ? "none"
                                        : "block",
                                  }}
                                >
                                  <i className="mdi mdi-check-circle me-2"></i>
                                  Approve
                                </Button>
                                <Button
                                  variant="danger"
                                  size="md"
                                  disabled={
                                    disableFields ||
                                    approvalState.excelApprovalStatus !==
                                      "pending_approval"
                                  }
                                  onClick={handleRejectExcel}
                                  className="fw-bold"
                                  style={{
                                    display:
                                      activeFilterTab === "approved" ||
                                      activeFilterTab === "rejected" ||
                                      (activeFilterTab === "all" &&
                                        getNormalizedStatus(
                                          approvalState.excelApprovalStatus,
                                        ) !== "pending")
                                        ? "none"
                                        : "block",
                                  }}
                                >
                                  <i className="mdi mdi-close-circle me-2"></i>
                                  Reject
                                </Button>
                                {getNormalizedStatus(
                                  approvalState.excelApprovalStatus,
                                ) !== "pending" && (
                                  <Button
                                    variant="warning"
                                    size="md"
                                    disabled={resetLoading}
                                    onClick={() => resetApproval("excel_file")}
                                    className="fw-bold mt-2 text-dark"
                                  >
                                    <i className="mdi mdi-refresh me-2"></i>
                                    Reset
                                  </Button>
                                )}
                              </div>

                              {/* Show rejection or approval with changes comments */}
                              {approvalState.excelApprovalStatus ===
                                "rejected" &&
                                getLatestComment(
                                  approvalState.excelComments,
                                ) && (
                                  <div
                                    className="mt-3 p-2 bg-light border rounded"
                                    style={{
                                      borderColor: "#dc3545",
                                    }}
                                  >
                                    <small className="text-muted d-block mb-1">
                                      <strong>Rejection Reason:</strong>
                                    </small>
                                    <small>
                                      {getLatestComment(
                                        approvalState.excelComments,
                                      )}
                                    </small>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                  {loading && (
                    <div className="mb-3 text-center text-muted">
                      Loading approval details...
                    </div>
                  )}

                  {/* Show message when no items match current filter */}
                  {!loading &&
                    approvalProductsData &&
                    filteredProducts.length === 0 &&
                    !shouldShowCard(approvalState.shippingAddressStatus) &&
                    !(
                      approvalData?.approvalExcelFile &&
                      shouldShowCard(approvalState.excelApprovalStatus)
                    ) && (
                      <div className="text-center py-4">
                        <div className="text-muted fs-5">
                          <i className="mdi mdi-filter-off me-2"></i>
                          No {activeFilterTab} items found
                        </div>
                        <Button
                          variant="link"
                          onClick={() => setActiveFilterTab("all")}
                          className="mt-2"
                        >
                          View all items
                        </Button>
                      </div>
                    )}

                  {/* {!loading && !approvalProductsData && (
                    <div className="text-muted">No approval data found</div>
                  )} */}
                  {/* Products Section------------- */}
                  <div className="">
                    {!loading &&
                      filteredProducts &&
                      filteredProducts.length > 0 && (
                        <div className="product-list-vertical">
                          {/* Product order (user, 2026-10-26). */}
                          {/* Normal-size buttons; the inactive one white with
                              the primary border, so it reads as a tab on the
                              grey page (user, 2026-10-26). Reorders on the
                              spot -- no reload. */}
                          {/* Section title above the order buttons (user,
                              2026-10-26). */}
                          <h4 className="fw-bold mt-3 mb-2">Product Approval</h4>
                          {/* "Sort By:" Style No# / Team Name, as wide as
                              their text -- on a row of their own, so the
                              list does not stretch them (user, 2026-10-26).
                              Only when some product has a team name;
                              otherwise Style No# order, nothing to choose. */}
                          {hasTeamNames && (
                            <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                              <span className="fw-bold">Sort By:</span>
                              <div
                                className="btn-group shadow-sm"
                                role="group"
                                aria-label="Product order"
                              >
                                {/* Default (sortId) first (user,
                                    2026-10-29); Team Name stays the one
                                    selected at first. */}
                                {[
                                  ["style", "Default"],
                                  ["team", "Team Name"],
                                ].map(([value, label]) => (
                                  <button
                                    key={value}
                                    type="button"
                                    aria-pressed={(productOrder || "team") === value}
                                    className={`btn py-2 text-uppercase fw-bold border-primary ${
                                      (productOrder || "team") === value
                                        ? "btn-primary"
                                        : "bg-white text-primary"
                                    }`}
                                    onClick={() => setProductOrder(value)}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                              {/* Team filter after the Sort By buttons, with arrows
                                  that step to the previous / next team; only
                                  when some product has a team name (user,
                                  2026-10-30). */}
                              {teamChoices.some(([value]) => value && value !== NO_TEAM) && (
                                <div className="input-group w-auto shadow-sm">
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary bg-white"
                                    aria-label="Previous team"
                                    onClick={() => stepTeam(-1)}
                                  >
                                    <i className="mdi mdi-chevron-left"></i>
                                  </button>
                                  <select
                                    className="form-select w-auto border-primary fw-bold"
                                    aria-label="Filter by team name"
                                    value={teamFilter}
                                    onChange={(e) => setTeamFilter(e.target.value)}
                                  >
                                    {teamChoices.map(([value, label]) => (
                                      <option key={value || "all"} value={value}>
                                        {label}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary bg-white"
                                    aria-label="Next team"
                                    onClick={() => stepTeam(1)}
                                  >
                                    <i className="mdi mdi-chevron-right"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          {orderedProducts.length === 0 && (
                            <div className="text-center text-muted py-4">
                              No products for this team on this tab.{" "}
                              <Button variant="link" className="p-0 align-baseline" onClick={() => setTeamFilter("")}>
                                Show all teams
                              </Button>
                            </div>
                          )}
                          {orderedProducts.map((prod, index) => {
                              const local =
                                approvalState.productApprovals.find(
                                  (p) => p.id === prod.id,
                                ) || {};
                              const currentStatus =
                                local.approveStatus ||
                                prod.approveStatus ||
                                "pending";
                              const latestProductComment = getLatestComment(
                                local.comments || prod.approvalComments,
                              );
                              const isStatusSet = currentStatus !== "pending";
                              // Use lotImagePath if available, otherwise use productImage
                              const imgUrl = prod.productImage || null;
                              let productSizes = [];
                              try {
                                if (prod.productSizes) {
                                  productSizes = sortProductSizes(prod.productSizes);
                                }
                              } catch (e) {
                                console.error(
                                  "Error parsing product sizes:",
                                  e,
                                );
                              }

                              // Get approval color names if available
                              const productColorIds = parseProductColorIds(
                                prod.approvalColors,
                              );
                              const approvalColorNames = productColorIds
                                .map((colorId) => colorMap[colorId]?.name)
                                .filter(Boolean)
                                .join(", ");

                              return (
                                <div
                                  className={`product-item-vertical rounded-3 mb-4 overflow-hidden ${
                                    currentStatus !== "pending"
                                      ? `bg-${getBadgeVariant(
                                          currentStatus,
                                        )} bg-opacity-10 border border-2 border-${getBadgeVariant(
                                          currentStatus,
                                        )}`
                                      : ""
                                  }`}
                                  key={prod.id}
                                >
                                  {/* Status Row at Top */}
                                  <div
                                    className={`w-100 text-center py-2 px-3 bg-${getBadgeVariant(
                                      currentStatus,
                                    )} ${
                                      getBadgeVariant(currentStatus) !==
                                        "warning" && " bg-opacity-25 text-white"
                                    }`}
                                  >
                                    <span
                                      className={`fw-bold text-${getBadgeVariant(
                                        currentStatus,
                                      )} ${
                                        getBadgeVariant(currentStatus) ===
                                        "warning"
                                          ? "text-white"
                                          : ""
                                      } text-uppercase`}
                                    >
                                      {formatStatusLabel(currentStatus) ||
                                        "PENDING"}
                                    </span>
                                  </div>

                                  <div className="product-header">
                                    {/* Number LEADS the row: it is what a
                                        customer quotes back ("item 3 is
                                        wrong"), so it should be the first
                                        thing the eye lands on rather than
                                        sitting behind the type. Style and
                                        description still read as one phrase at
                                        the end. Mirrors the WO PDF header. */}
                                    {/* The product's own number on the
                                        work order (sortId), not its place
                                        in the list -- under Team Name the
                                        numbers run out of order (user,
                                        2026-10-27). */}
                                    <div className="product-number-label">
                                      {prod?.sortId ?? index + 1}
                                    </div>
                                    <div className="product-type-label">
                                      {prod?.productTypes?.productTypes}
                                    </div>
                                    <div className="product-title flex-center justify-content-start">
                                      <span>
                                        {prod.productStyle || "N/A"} :{" "}
                                        {prod.productDescription || ""}
                                      </span>
                                      {/* Pushed to the end of the row by
                                          ms-auto; renders nothing at all when
                                          the catalogue has none of the three. */}
                                      <ProductInformationButtons
                                        information={prod.productInformation}
                                        onError={(message) =>
                                          dispatch(
                                            setNotification({
                                              message,
                                              type: "error",
                                            }),
                                          )
                                        }
                                      />
                                    </div>
                                  </div>

                                  <div className="product-body">
                                    <div className="product-info">
                                      {/* Color FIRST, on its own full-width
                                          row, then Fabric + Team Name -- the
                                          same order as the work order PDF
                                          (WOPdfProducts) so the card on screen
                                          and the printout read identically.
                                          Colour stays full width because with
                                          approval colours resolved to names it
                                          is routinely the longest value on the
                                          card.

                                          Every cell carries a bottom border,
                                          so whichever row sits last (now the
                                          Fabric/Team row) still draws a
                                          full-width divider above the image --
                                          the PDF lays it out this way and reads
                                          fine. */}
                                      <div className="product-details-row">
                                        <div className="product-detail-col product-detail-col-full">
                                          <div
                                            className="detail-label"
                                            style={{ minWidth: "120px" }}
                                          >
                                            Color:
                                          </div>
                                          {/* no w-100 here: Bootstrap's
                                              width:100% !important would beat
                                              the 80% and shove the value out
                                              of alignment with Fabric below. */}
                                          <div className="detail-value">
                                            {approvalColorNames ||
                                              prod.productColor ||
                                              "N/A"}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="product-details-row">
                                        {/* The divider before Team Name lives
                                            on the value cell, NOT on the
                                            column. On the column its 1px
                                            shrinks the content box, so the
                                            label's 40% resolves against 1px
                                            less than the full-width Color row
                                            above and the two borders land
                                            half a pixel apart. */}
                                        <div className="product-detail-col">
                                          <div
                                            className="detail-label"
                                            style={{ minWidth: "120px" }}
                                          >
                                            Fabric:
                                          </div>
                                          {/* no w-100, for the same reason as
                                              the Color row above: width:100%
                                              !important overflows the column,
                                              flex shrinks the LABEL to its
                                              min-width floor, and the Fabric
                                              border lands 2px left of the
                                              Color border. */}
                                          <div className="detail-value detail-value-divider">
                                            {prod.productFabric || "N/A"}
                                          </div>
                                        </div>
                                        <div className="product-detail-col">
                                          <div className="detail-label">
                                            Team Name:
                                          </div>
                                          <div className="detail-value">
                                            {prod.teamName || "N/A"}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="product-image-container position-relative">
                                        {imgUrl ? (
                                          <div
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onFullViewImg(
                                                imgUrl,
                                                galleryImages,
                                              );
                                            }}
                                            style={{ cursor: "pointer" }}
                                            disabled={disableFields}
                                          >
                                            <ImageWIthSignedUrl
                                              path={imgUrl}
                                              alt={
                                                prod.productDescription ||
                                                prod.productStyle
                                              }
                                              imgStyle={{
                                                maxHeight: "200px",
                                                width: "100%",
                                                objectFit: "contain",
                                              }}
                                            />
                                          </div>
                                        ) : (
                                          <div className="img-placeholder d-flex flex-column justify-content-center align-items-center">
                                            <div className="placeholder-icon">
                                              🖼️
                                            </div>
                                            <div className="text-muted">
                                              No Image
                                            </div>
                                          </div>
                                        )}
                                        {imgUrl && (
                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="download-btn"
                                            onClick={() =>
                                              onFullViewImg(
                                                imgUrl,
                                                galleryImages,
                                              )
                                            }
                                            disabled={lockApproval}
                                            aria-label="Zoom image"
                                          >
                                            <i className="mdi mdi-magnify-plus"></i>
                                          </Button>
                                        )}
                                      </div>

                                      {/* Product-specific colors */}
                                      {(() => {
                                        const productColorIds =
                                          parseProductColorIds(
                                            prod.approvalColors,
                                          );

                                        if (!productColorIds.length)
                                          return null;

                                        return (
                                          <div className="px-3 py-1">
                                            <hr className="mt-0" />

                                            {loadingColors ? (
                                              <div className="text-center py-2">
                                                <div
                                                  className="spinner-border text-primary"
                                                  role="status"
                                                  style={{
                                                    width: "1.75rem",
                                                    height: "1.75rem",
                                                  }}
                                                >
                                                  <span className="visually-hidden">
                                                    Loading colors...
                                                  </span>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="d-flex flex-wrap gap-3">
                                                {productColorIds.map(
                                                  (colorId) => {
                                                    const color =
                                                      colorMap[colorId];

                                                    if (!color) {
                                                      return (
                                                        <div
                                                          key={colorId}
                                                          className="text-muted small"
                                                        >
                                                          Color #{colorId}
                                                        </div>
                                                      );
                                                    }

                                                    return (
                                                      <div
                                                        key={colorId}
                                                        className="d-flex flex-column align-items-center gap-2"
                                                        style={{
                                                          cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                          onFullViewImg(
                                                            color?.fileName,
                                                          )
                                                        }
                                                      >
                                                        <div
                                                          style={{
                                                            width: "60px",
                                                            height: "60px",
                                                            border:
                                                              "2px solid #ddd",
                                                            borderRadius: "8px",
                                                            overflow: "hidden",
                                                            boxShadow:
                                                              "0 2px 4px rgba(0,0,0,0.08)",
                                                          }}
                                                        >
                                                          <ImageWIthSignedUrl
                                                            path={
                                                              color?.fileName
                                                            }
                                                            imgStyle={{
                                                              width: "100%",
                                                              height: "100%",
                                                              objectFit:
                                                                "cover",
                                                            }}
                                                            alt={color.name}
                                                          />
                                                        </div>
                                                        <div
                                                          className="text-center fw-semibold"
                                                          style={{
                                                            maxWidth: "100px",
                                                            fontSize: "14px",
                                                          }}
                                                        >
                                                          {color.name}
                                                        </div>
                                                      </div>
                                                    );
                                                  },
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>

                                    <div className="product-sizes">
                                      <div className="qty-info pb-1 mb-1">
                                        <div className="qty-label fw-bold">
                                          Qty: {prod.totalQuantity || 0}
                                        </div>
                                      </div>

                                      <div className="size-details">
                                        <div className="size-title">
                                          Size Detail
                                        </div>
                                        {productSizes.length > 0 ? (
                                          <div className="size-list size-scrollable">
                                            {productSizes
                                              .sort(
                                                (a, b) => a.sortId - b.sortId,
                                              )
                                              .map((size) => (
                                                <div
                                                  key={size.id}
                                                  className="size-item"
                                                >
                                                  {size.size} = {size.quantity}
                                                </div>
                                              ))}
                                          </div>
                                        ) : (
                                          <div className="text-muted">
                                            No size data
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    <div
                                      style={{
                                        width: "300px",
                                      }}
                                      className="product-approval product-approval-responsive"
                                    >
                                      <div className="approval-status-control">
                                        {/* Approve/Reject Buttons */}
                                        <div className="d-grid gap-2">
                                          <Button
                                            variant="success"
                                            size="md"
                                            disabled={
                                              disableFields || isStatusSet
                                            }
                                            onClick={() =>
                                              handleApproveProduct(prod)
                                            }
                                            className="fw-bold"
                                            style={{
                                              display:
                                                activeFilterTab ===
                                                  "approved" ||
                                                activeFilterTab ===
                                                  "rejected" ||
                                                (activeFilterTab === "all" &&
                                                  getNormalizedStatus(
                                                    currentStatus,
                                                  ) !== "pending")
                                                  ? "none"
                                                  : "block",
                                            }}
                                          >
                                            <i className="mdi mdi-check-circle me-2"></i>
                                            Approve
                                          </Button>
                                          <Button
                                            variant="danger"
                                            size="md"
                                            disabled={
                                              disableFields || isStatusSet
                                            }
                                            onClick={() =>
                                              handleRejectProduct(prod)
                                            }
                                            className="fw-bold"
                                            style={{
                                              display:
                                                activeFilterTab ===
                                                  "approved" ||
                                                activeFilterTab ===
                                                  "rejected" ||
                                                (activeFilterTab === "all" &&
                                                  getNormalizedStatus(
                                                    currentStatus,
                                                  ) !== "pending")
                                                  ? "none"
                                                  : "block",
                                            }}
                                          >
                                            <i className="mdi mdi-close-circle me-2"></i>
                                            Reject
                                          </Button>
                                          {getNormalizedStatus(
                                            currentStatus,
                                          ) !== "pending" && (
                                            <Button
                                              variant="warning"
                                              size="md"
                                              disabled={resetLoading}
                                              onClick={() =>
                                                resetApproval(
                                                  "product",
                                                  prod.id,
                                                )
                                              }
                                              className="fw-bold mt-2 text-dark"
                                            >
                                              <i className="mdi mdi-refresh me-2"></i>
                                              Reset
                                            </Button>
                                          )}
                                        </div>

                                        {/* Show rejection or approval with changes comments */}
                                        {currentStatus === "rejected" &&
                                          latestProductComment && (
                                            <div
                                              className="mt-3 p-2 bg-light border rounded"
                                              style={{
                                                borderColor: "#dc3545",
                                              }}
                                            >
                                              <small className="text-muted d-block mb-1">
                                                <strong>
                                                  Rejection Reason:
                                                </strong>
                                              </small>
                                              <small>
                                                {latestProductComment}
                                              </small>
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                  {prod.notes && (
                                    <div className="product-comments">
                                      <strong>COMMENTS:</strong>{" "}
                                      {stripHtml(prod.notes) ||
                                        "No comments"}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      )}
                  </div>
                </Col>
              </Row>
              <div className="approve-all-cta-wrap">
                <Button
                  variant="success"
                  size="md"
                  disabled={disableFields || approvalStats.pending === 0}
                  onClick={handleApproveAll}
                  className="fw-bold px-4 btn-mobile-full approve-all-cta-btn"
                >
                  <i className="mdi mdi-check-all me-2"></i>
                  Approve All
                </Button>
              </div>
            </div>

            {/* End of responsive container */}

            {/*   Modal for detailed view or image (if needed)
             ********************************************* */}
            <Modal
              show={showFullViewImgModal}
              size="xl"
              centered
              dialogClassName="image-view-modal"
              contentClassName="image-view-modal-content"
              onHide={() => onHideFullViewImg()}
            >
              <Modal.Header className="image-modal-header">
                <Modal.Title>
                  Image View
                  {fullViewList.length > 1 && (
                    <span className="text-muted fs-6 ms-2">
                      {fullViewIndex + 1} / {fullViewList.length}
                    </span>
                  )}
                </Modal.Title>
                {/* Prev/next live in the header, top-right, next to Close.
                    Shown only when there is more than one image to move
                    between; they wrap at the ends. */}
                <div className="d-flex align-items-center gap-2">
                  {fullViewList.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={showPrevImg}
                        aria-label="Previous image"
                        title="Previous image"
                      >
                        <i className="mdi mdi-chevron-left"></i>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={showNextImg}
                        aria-label="Next image"
                        title="Next image"
                      >
                        <i className="mdi mdi-chevron-right"></i>
                      </button>
                    </>
                  )}
                  <button
                    className="btn-close"
                    onClick={() => onHideFullViewImg()}
                    aria-label="Close"
                  ></button>
                </div>
              </Modal.Header>
              <Modal.Body className="image-modal-body p-0">
                {fullViewImgUrl ? (
                  <div className="image-viewer-container">
                    <div className="zoom-instructions">
                      <span>Scroll Or Pinch To Zoom</span>
                    </div>
                    <TransformWrapper
                      // Remount on image change so a zoomed-in view resets to
                      // fit when you page to the next image.
                      key={fullViewIndex}
                      initialScale={1}
                      minScale={0.5}
                      maxScale={8}
                      centerOnInit={true}
                      centerZoomedOut={true}
                      doubleClick={{ mode: "reset" }}
                    >
                      {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                        <>
                          <TransformComponent
                            wrapperClassName="transform-component-wrapper"
                            contentClassName="transform-component-content"
                          >
                            <ImageWIthSignedUrl
                              path={fullViewImgUrl}
                              imgStyle={{
                                height: "600px",
                                width: "auto",
                                objectFit: "contain",
                              }}
                              imgClasses="zoomable-image bg-light border rounded-3"
                            />
                          </TransformComponent>
                          <div className="zoom-tip mt-2 gap-3 text-muted d-flex  text-center justify-content-center align-items-center">
                            <h5 className="">
                              Tip: Double-click to reset view
                            </h5>
                            <Controls />
                          </div>
                        </>
                      )}
                    </TransformWrapper>
                  </div>
                ) : (
                  <div className="no-image-placeholder">
                    <div className="placeholder-icon">🖼️</div>
                    <p>No image available</p>
                  </div>
                )}
              </Modal.Body>
            </Modal>

            {/* Reset Rejected Items Modal (viewOnly) */}
            <Modal
              show={showResetModal}
              onHide={() => setShowResetModal(false)}
              centered
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title>Approval Items</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {resetModalItems && resetModalItems.length > 0 ? (
                  <Table hover responsive>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resetModalItems.map((it) => {
                        const isPending =
                          it.status === "pending" ||
                          it.status === "pending_approval";
                        return (
                          <tr key={`${it.type}-${it.id || "x"}`}>
                            <td>{it.label}</td>
                            <td className="text-capitalize">
                              {it.type.replaceAll("_", " ")}
                            </td>
                            <td>
                              <span
                                className={`badge bg-${getBadgeVariant(it.status)} ${
                                  it.status === "pending_approval" ||
                                  it.status === "pending"
                                    ? "text-dark"
                                    : ""
                                }`}
                              >
                                {formatStatusLabel(it.status)}
                              </span>
                            </td>
                            <td className="text-end">
                              {!isPending && (
                                <Button
                                  size="sm"
                                  variant="danger"
                                  disabled={resetLoading}
                                  onClick={() => resetApproval(it.type, it.id)}
                                >
                                  <i className="mdi mdi-refresh me-1"></i>
                                  Reset
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted">No items found</div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowResetModal(false)}
                >
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Rejection Modal */}
            <RejectionModal
              show={showRejectionModal}
              onHide={() => {
                setShowRejectionModal(false);
                setCurrentRejectionItem(null);
              }}
              onConfirm={confirmRejection}
              itemName={currentRejectionItem?.name || ""}
              allowFileUpload={currentRejectionItem?.type === "excel"}
              fileLabel="Attach Corrected Excel File"
              fileAccept=".xlsx,.xls"
            />

            <Modal
              show={showApproveAllDateModal}
              onHide={() => setShowApproveAllDateModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>In-Hand Date Required</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="mb-2">
                  Please select the In-Hand Date to continue with Approve All.
                </p>
                <input
                  type="date"
                  className="form-control"
                  min={minInHandDate}
                  value={approveAllDate}
                  onChange={(e) => setApproveAllDate(e.target.value)}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowApproveAllDateModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  onClick={handleApproveAllFromModal}
                  disabled={!approveAllDate || loading}
                >
                  Submit & Approve All
                </Button>
              </Modal.Footer>
            </Modal>

            {/* Customer address edit. Re-fetches on save rather than patching
                state locally: the server also appends to the approval's
                comment history, so a local patch would leave the page showing
                a new address with no record of the change beside it. */}
            {editingAddress && (
              <CustomerAddressEditModal
                currentShipping={parsedShippingAddress}
                shipmentMethods={shipmentMethodsList}
                currentShipmentMethodId={
                  approvalProductsData?.shippingMethod?.id ??
                  approvalProductsData?.shipmentMethod ??
                  null
                }
                token={token}
                editedBy={[parsedContact?.firstName, parsedContact?.lastName]
                  .filter(Boolean)
                  .join(" ")}
                onHide={() => setEditingAddress(false)}
                onSaved={() => {
                  setEditingAddress(false);
                  getApprovalDetails(token);
                }}
              />
            )}
          </section>
        </section>
      </Container>
    </>
  );
}
